/**
 * Connections.tsx — REPLACEMENT page shell (thin). All lists, drawers, and
 * dialogs now live in src/components/network/ (extraction map in the notes
 * file). This shell adds DEEP-LINK support so notification links resolve:
 *
 *   /connections/handshakes/:handshakeId     → handshakes tab + drawer open
 *   /connections/announcements/:announcementId → announcements tab
 *
 * App.tsx: register BOTH extra paths on the same element as /connections
 * (snippet in the notes file). Closing a deep-linked drawer navigates back
 * to /connections so the URL stays truthful.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Plus,
  Building2,
  Handshake as HandshakeIcon,
  Megaphone,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ConnectionsList } from "@/components/network/ConnectionsList";
import { HandshakesList } from "@/components/network/HandshakesList";
import { AnnouncementsList } from "@/components/network/AnnouncementsList";
import { ConnectionDrawer } from "@/components/network/ConnectionDrawer";
import { HandshakeDrawer } from "@/components/network/HandshakeDrawer";
import { NewConnectionDialog } from "@/components/network/NewConnectionDialog";
import { NewHandshakeDialog } from "@/components/network/NewHandshakeDialog";
import { CreateAnnouncementDialog } from "@/components/network/AnnouncementDialogs";

export default function Connections() {
  const navigate = useNavigate();
  const params = useParams<{ handshakeId?: string; announcementId?: string }>();

  const [activeTab, setActiveTab] = useState("connections");
  const [openConnectionId, setOpenConnectionId] = useState<string | null>(null);
  const [openHandshakeId, setOpenHandshakeId] = useState<string | null>(null);
  const [newConnectionOpen, setNewConnectionOpen] = useState(false);
  const [newHandshakeOpen, setNewHandshakeOpen] = useState(false);
  const [newAnnouncementOpen, setNewAnnouncementOpen] = useState(false);

  // Deep-links from notifications: /connections/handshakes/:id opens the
  // drawer directly; /connections/announcements/:id lands on the tab.
  useEffect(() => {
    if (params.handshakeId) {
      setActiveTab("handshakes");
      setOpenHandshakeId(params.handshakeId);
    } else if (params.announcementId) {
      setActiveTab("announcements");
    }
  }, [params.handshakeId, params.announcementId]);

  const closeHandshakeDrawer = () => {
    setOpenHandshakeId(null);
    if (params.handshakeId) navigate("/connections", { replace: true });
  };

  return (
    <div className="container mx-auto py-6 max-w-6xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Connections</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Trusted distributor and vendor relationships. Send and receive
            handshakes, broadcast announcements, track every exchange.
          </p>
        </div>
        <div className="flex gap-2">
          {activeTab === "handshakes" && (
            <Button variant="outline" onClick={() => setNewHandshakeOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New handshake
            </Button>
          )}
          {activeTab === "announcements" && (
            <Button
              variant="outline"
              onClick={() => setNewAnnouncementOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              New announcement
            </Button>
          )}
          <Button onClick={() => setNewConnectionOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New connection
          </Button>
        </div>
      </header>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="connections" className="gap-2">
            <Building2 className="h-4 w-4" />
            Connections
          </TabsTrigger>
          <TabsTrigger value="handshakes" className="gap-2">
            <HandshakeIcon className="h-4 w-4" />
            Handshakes
          </TabsTrigger>
          <TabsTrigger value="announcements" className="gap-2">
            <Megaphone className="h-4 w-4" />
            Announcements
          </TabsTrigger>
        </TabsList>

        <TabsContent value="connections" className="mt-6">
          <ConnectionsList onOpen={setOpenConnectionId} />
        </TabsContent>

        <TabsContent value="handshakes" className="mt-6">
          <HandshakesList onOpen={setOpenHandshakeId} />
        </TabsContent>

        <TabsContent value="announcements" className="mt-6">
          <AnnouncementsList />
        </TabsContent>
      </Tabs>

      <ConnectionDrawer
        id={openConnectionId}
        onClose={() => setOpenConnectionId(null)}
      />
      <HandshakeDrawer id={openHandshakeId} onClose={closeHandshakeDrawer} />

      <NewConnectionDialog
        open={newConnectionOpen}
        onClose={() => setNewConnectionOpen(false)}
      />
      <NewHandshakeDialog
        open={newHandshakeOpen}
        onClose={() => setNewHandshakeOpen(false)}
      />
      <CreateAnnouncementDialog
        open={newAnnouncementOpen}
        onClose={() => setNewAnnouncementOpen(false)}
      />
    </div>
  );
}
